import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../config/supabase';
import OpenAI from 'openai';
import { decryptApiKey } from '../utils/encryption';

const OpenAIContext = createContext();

export const OpenAIProvider = ({ children }) => {
  const { user } = useAuth();
  const [openai, setOpenai] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // เรียกใช้ initializeOpenAI ครั้งแรกเมื่อโหลดหน้า
  // เพื่อให้ระบบทำงานได้กับ API key ที่มีอยู่แล้ว
  useEffect(() => {
    if (user) {
      initializeOpenAI();
    }
  }, [user]);

  const initializeOpenAI = async () => {
    try {
      setLoading(true);
      setError(null);

      // ดึง API key จากฐานข้อมูล
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select(`
          openai (
            api_key,
            is_active
          )
        `)
        .eq('id', user.id)
        .single();

      if (fetchError) throw fetchError;

      const encryptedApiKey = data?.openai?.[0]?.api_key;
      const isActive = data?.openai?.[0]?.is_active;

      if (!encryptedApiKey || !isActive) {
        throw new Error('กรุณาตั้งค่า OpenAI API Key ใน<a href="/profile">หน้าโปรไฟล์</a>');
      }
      
      // ถอดรหัส API key ก่อนใช้งาน
      const apiKey = decryptApiKey(encryptedApiKey);

      // สร้าง OpenAI instance
      const openaiInstance = new OpenAI({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true // เนื่องจากเราใช้งานบน browser
      });

      setOpenai(openaiInstance);
    } catch (err) {
      console.error('Error initializing OpenAI:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ฟังก์ชันคำนวณค่าใช้จ่าย
  const calculateCost = (usage) => {
    const prompt_tokens = usage.prompt_tokens;
    const completion_tokens = usage.completion_tokens;
    const total_tokens = usage.total_tokens;

    // คำนวณราคา (GPT-4o-mini rates)
    const input_cost = (prompt_tokens / 1000) * 0.00015;  // $0.00015 per 1K input tokens
    const output_cost = (completion_tokens / 1000) * 0.0006;  // $0.0006 per 1K output tokens
    const total_cost_usd = input_cost + output_cost;
    const total_cost_thb = total_cost_usd * 35.5;  // อัตราแลกเปลี่ยนโดยประมาณ

    return {
      prompt_tokens,
      completion_tokens,
      total_tokens,
      input_cost_usd: input_cost,
      output_cost_usd: output_cost,
      total_cost_usd: total_cost_usd,
      total_cost_thb: total_cost_thb
    };
  };

  // ฟังก์ชันสำหรับสร้าง prompt
  const generatePrompt = async (model, keyword, amount = 1) => {
    // ถ้ายังไม่ได้เริ่มต้น OpenAI ให้เริ่มต้นก่อน
    if (!openai) {
      try {
        await initializeOpenAI();
      } catch (error) {
        throw new Error('OpenAI ยังไม่พร้อมใช้งาน กรุณาตรวจสอบ API key ใน<a href="/profile">หน้าโปรไฟล์</a>');
      }
    }
    
    // ตรวจสอบอีกครั้งหลังจากเริ่มต้น
    if (!openai) {
      throw new Error('OpenAI ยังไม่พร้อมใช้งาน กรุณาตรวจสอบ API key ใน<a href="/profile">หน้าโปรไฟล์</a>');
    }

    try {
      // เลือก system prompt ตามโมเดล
      let systemPrompt = '';
      if (model === 'Polar Bear') {
        systemPrompt = `You are a commercial stock photo keyword generator.
Your task is to generate keywords for the given input word.
Each keyword should be 2-3 words long and related to stock photography.

Rules:
- Generate EXACTLY ${amount} keyword(s), no more no less
- Only respond with keywords, no explanations
- Separate keywords with |
- STRICTLY English language only, DO NOT use any other languages
- Must be related to the input word
- Focus on visual and commercial aspects

Example input with amount=1: "dog"
Example output: "happy puppy"

Example input with amount=3: "dog"
Example output: "happy puppy | playful canine | loyal companion"

Do not explain or add any other text. Just return the keywords.`;
      } else if (model === 'Chicken') {
        systemPrompt = `Create ${amount} detailed stock photo prompt(s) (max 120 chars each).
Must be related to the input keyword.
Include: main subject, action/pose, mood, lighting, camera angle, setting.
Make it artistic but keep commercial value.

Rules:
- Generate EXACTLY ${amount} prompt(s), no more no less
- Separate prompts with |
- STRICTLY English language only, DO NOT use any other languages
- Must be related to the input word
- Focus on visual and commercial aspects

Example input with amount=1: "pet food"
Example output: "Playful cat enjoying gourmet meal, whiskers twitching, cozy kitchen corner, soft window light, eye-level shot"

Example input with amount=2: "pet food"
Example output: "Playful cat enjoying gourmet meal, whiskers twitching, cozy kitchen corner, soft window light, eye-level shot | Elegant dog savoring premium kibble, modern kitchen setting, warm backlighting, low angle perspective"

Do not explain or add any other text. Just return the prompts separated by |.`;
      }

      const completion = await openai.chat.completions.create({
        model: "gpt-4o-mini",
        messages: [
          {
            role: "system",
            content: systemPrompt
          },
          {
            role: "user",
            content: keyword
          }
        ],
        temperature: 0.8,
      });

      // คำนวณค่าใช้จ่าย
      const cost = calculateCost(completion.usage);

      return {
        content: completion.choices[0].message.content,
        cost
      };
    } catch (err) {
      console.error('Error generating prompt:', err);
      throw new Error('ไม่สามารถสร้าง prompt ได้: ' + err.message);
    }
  };

  const value = {
    openai,
    loading,
    error,
    generatePrompt,
    initializeOpenAI
  };

  return (
    <OpenAIContext.Provider value={value}>
      {children}
    </OpenAIContext.Provider>
  );
};

export const useOpenAI = () => {
  const context = useContext(OpenAIContext);
  if (!context) {
    throw new Error('useOpenAI must be used within an OpenAIProvider');
  }
  return context;
};
